import {FormEvent, useEffect, useRef, useState} from "react";
import {DeviceLogCategory, GetDeviceLogs_DeviceLogDto as DeviceLogDto, GetDeviceLogs_DeviceLogsPageDto as DeviceLogsPageDto, LogsService} from "../../../api";
import {getApiErrorMessage} from "../../../services/ApiErrorMessage";
import {exportDeviceLogs, ExportControl, supportsDirectSave} from "./exportDeviceLogs";
import "./DeviceLogs.scss";

type Props = {deviceId: string};
type Filters = {search: string; from: string; to: string; size: number; category: DeviceLogCategory};
type Cursor = {time?: string; id?: string};
type View = "explain" | "raw" | "both";
const initialFilters: Filters = {search: "", from: "", to: "", size: 100, category: "All"};
const categories: {value: DeviceLogCategory; label: string}[] = [
  {value: "All", label: "Все события и команды"}, {value: "Door", label: "Дверь"},
  {value: "Impact", label: "Удар"}, {value: "Mechanism", label: "Механизмы"}, {value: "Connection", label: "Подключения и связь"}
];
const dateText = (value?: string) => value ? new Date(value).toLocaleString("ru-RU") : "";
const utc = (value: string) => value ? new Date(value).toISOString() : undefined;

const kinds: Record<string, string> = {
  success: "Успешный ответ", error: "Ошибка", command: "Команда",
  response: "Ответ", ping: "Связь", event: "Событие", unknown: "Без расшифровки"
};

const Original = ({log, expanded}: {log: DeviceLogDto; expanded?: boolean}) =>
  expanded ? <pre className="device-logs__raw">{log.data || "(пустая запись)"}</pre> :
    <details className="device-logs__original">
      <summary>Исходная запись</summary><pre className="device-logs__raw">{log.data || "(пустая запись)"}</pre>
    </details>;

const LogsHistory = ({deviceId}: Props) => {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [view, setView] = useState<View>("explain");
  const [page, setPage] = useState<DeviceLogsPageDto | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [cursors, setCursors] = useState<Cursor[]>([{}]);
  const [pageNumber, setPageNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [exportMessage, setExportMessage] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportAmount, setExportAmount] = useState("1000");
  const [customAmount, setCustomAmount] = useState("1000");
  const [confirmAll, setConfirmAll] = useState(false);
  const [exportGoal, setExportGoal] = useState<number | undefined>();
  const listRef = useRef<HTMLDivElement>(null);
  const scrollAfterLoad = useRef(false);
  const activeExport = useRef<ExportControl | null>(null);
  const snapshot = useRef<string | undefined>(undefined);

  const reset = (nextFilters: Filters = filters) => {
    snapshot.current = undefined;
    setPage(null);
    setTotal(null);
    setCursors([{}]);
    setPageNumber(0);
    setExportMessage("");
    if (activeExport.current) {
      activeExport.current.discard = true;
      activeExport.current.request?.cancel();
    }
    setFilters(nextFilters);
    setRefresh(x => x + 1);
  };

  // Ignore responses from a previous page/filter, even if cancellation arrives too late.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const cursor = cursors[pageNumber] ?? {};
    const request = LogsService.getApiLogsPage(deviceId, filters.size, utc(filters.from), utc(filters.to),
      filters.search || undefined, snapshot.current, cursor.time, cursor.id, filters.category, true);
    request.then(result => {
      if (!active) return;
      snapshot.current = result.snapshotAt;
      setPage(result);
      if (result.total != null) setTotal(result.total);
      if (scrollAfterLoad.current) {
        listRef.current?.scrollIntoView({block: "start"});
        scrollAfterLoad.current = false;
      }
    }).catch(err => {
      if (active && !err?.isCancelled) setError(getApiErrorMessage(err, "Не удалось загрузить логи"));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; request.cancel(); };
  }, [deviceId, filters, refresh, cursors, pageNumber]);

  useEffect(() => () => {
    if (activeExport.current) { activeExport.current.discard = true; activeExport.current.request?.cancel(); }
  }, []);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    if (exporting) return;
    if (draft.from && draft.to && new Date(draft.from) > new Date(draft.to)) {
      setFormError("Начало периода должно быть раньше его окончания.");
      return;
    }
    setFormError("");
    reset({...draft, search: draft.search.trim()});
  };

  const next = () => {
    if (!page?.hasMore || !page.nextTime || !page.nextId) return;
    scrollAfterLoad.current = true;
    setPage(null);
    setCursors([...cursors.slice(0, pageNumber + 1), {time: page.nextTime, id: page.nextId}]);
    setPageNumber(pageNumber + 1);
  };

  const exportAll = async () => {
    if (activeExport.current) return;
    const limit = exportAmount === "all" ? undefined : Number(exportAmount === "custom" ? customAmount : exportAmount);
    if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 1 || limit > 5000000)) {
      setExportMessage("Введите целое количество от 1 до 5 000 000.");
      return;
    }
    if (exportAmount === "all" && !confirmAll) return;
    const control: ExportControl = {stop: false, discard: false};
    activeExport.current = control;
    setExporting(true);
    setExportCount(0);
    setExportGoal(limit ?? total ?? undefined);
    setExportMessage("");
    try {
      const result = await exportDeviceLogs({deviceId, from: utc(filters.from), to: utc(filters.to),
        search: filters.search || undefined, category: filters.category, snapshotAt: snapshot.current,
        limit, total, control, onProgress: setExportCount});
      if (control.discard) return;
      setExportMessage(result.cancelled ? "Выгрузка отменена; записей не сохранено." :
        result.stopped ? `Выгрузка остановлена. Сохранена часть: ${result.count.toLocaleString("ru-RU")} записей.` :
          `Сохранено ${result.count.toLocaleString("ru-RU")} записей.${result.exhausted ? " Это вся доступная выборка." : ""}`);
    } catch (err) {
      if (!control.discard) setExportMessage(`${getApiErrorMessage(err, "Ошибка выгрузки")}. Выгрузка не завершена.`);
    } finally {
      activeExport.current = null;
      setExporting(false);
    }
  };

  const logs = page?.logs ?? [];
  const first = pageNumber * filters.size + 1;
  const pagination = <div className="device-logs__pagination">
    <button type="button" disabled={loading || exporting || pageNumber === 0} onClick={() => {scrollAfterLoad.current = true; setPage(null); setPageNumber(x => x - 1);}}>Более новые</button>
    <span>Страница {pageNumber + 1}</span>
    <button type="button" className="device-logs__primary" disabled={loading || exporting || !page?.hasMore || !!error} onClick={next}>Загрузить более ранние</button>
  </div>;
  return (
    <section className="device-logs" aria-label="Логи системы">
      <header className="device-logs__heading">
        <div><h2>История обмена со станцией</h2><p>Все сохранённые записи. Пояснения отдельно, исходные данные без изменений.</p></div>
        <button type="button" disabled={loading || exporting} onClick={() => reset()}>Обновить</button>
      </header>
      <form className="device-logs__filters" onSubmit={apply}>
        <label className="device-logs__search">Поиск по всей истории
          <input placeholder="Команда, адрес или текст исходного лога" maxLength={200}
                 value={draft.search} onChange={e => setDraft({...draft, search: e.target.value})}/>
        </label>
        <label>Тип события<select value={draft.category} onChange={e => setDraft({...draft, category: e.target.value as DeviceLogCategory})}>
          {categories.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
        </select></label>
        <label>С даты<input type="datetime-local" value={draft.from} onChange={e => setDraft({...draft, from: e.target.value})}/></label>
        <label>По дату<input type="datetime-local" value={draft.to} onChange={e => setDraft({...draft, to: e.target.value})}/></label>
        <label>На странице<select value={draft.size} onChange={e => setDraft({...draft, size: Number(e.target.value)})}>
          {[100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
        </select></label>
        <button type="submit" className="device-logs__primary" disabled={exporting}>Показать</button>
        <button type="button" disabled={exporting} onClick={() => {setDraft(initialFilters); reset({...initialFilters}); setFormError("");}}>Сбросить</button>
      </form>
      {formError && <p role="alert" className="device-logs__error">{formError}</p>}
      <div className="device-logs__toolbar">
        <div className="device-logs__views" role="group" aria-label="Вид логов">
          {([["explain", "Понятно по-русски"], ["raw", "Исходные логи"], ["both", "Оба вида"]] as [View, string][]).map(([key, label]) =>
            <button type="button" key={key} aria-pressed={view === key} onClick={() => setView(key)}>{label}</button>)}
        </div>
        <button type="button" aria-expanded={exportOpen} onClick={() => setExportOpen(x => !x)}>Настроить выгрузку</button>
      </div>
      {exportOpen && <section className="device-logs__export-settings" aria-label="Настройки выгрузки">
        <h3>Сколько записей скачать?</h3>
        <p>Самые новые записи из применённой выборки, независимо от открытой страницы. Даты, поиск и тип события учитываются.</p>
        <div className="device-logs__export-controls">
          <label>Количество записей<select disabled={exporting} value={exportAmount} onChange={e => {setExportAmount(e.target.value); setConfirmAll(false);}}>
            <option value="1000">Последние 1 000</option><option value="5000">Последние 5 000</option>
            <option value="10000">Последние 10 000</option><option value="custom">Своё количество</option>
            <option value="all">Вся выборка</option>
          </select></label>
          {exportAmount === "custom" && <label>Своё количество<input disabled={exporting} type="number" min={1} max={5000000} step={1}
            value={customAmount} onChange={e => setCustomAmount(e.target.value)}/></label>}
          <button type="button" className="device-logs__primary" disabled={exporting || loading || !page || !!error || (exportAmount === "all" && !confirmAll)}
            onClick={exportAll}>Начать скачивание</button>
        </div>
        {exportAmount === "all" && <label className="device-logs__export-warning">
          <input type="checkbox" disabled={exporting} checked={confirmAll} onChange={e => setConfirmAll(e.target.checked)}/>
          {total != null ? `В выборке ${total.toLocaleString("ru-RU")} записей. ` : "Количество совпадений заранее не подсчитывается. "}
          Большая выгрузка может занять длительное время. Скачать всё.
        </label>}
        <p>{supportsDirectSave() ? "До 10 000 записей файл скачивается обычным способом. Для большей выгрузки браузер предложит выбрать файл и будет записывать его порциями, без накопления всего архива в памяти." :
          "В этом браузере файл готовится в памяти: до 50 000 записей и 64 МБ. Для большего архива выберите период или настольный браузер с прямой записью в файл."}</p>
      </section>}
      <div className="device-logs__status" aria-live="polite">
        <span>{loading ? "Загружаю историю…" : error ? "Не удалось загрузить страницу" : logs.length ? `Записи ${first.toLocaleString("ru-RU")}–${(first + logs.length - 1).toLocaleString("ru-RU")}${total != null ? ` из ${total.toLocaleString("ru-RU")}` : " по выбранному фильтру"}` : "Записей не найдено"}</span>
        <span>{filters.from || filters.to ? "Выбранный период" : "За всё время"} · новые сначала</span>
      </div>
      {exporting && <div className="device-logs__export" role="status">
        <span>Подготовлено {exportCount.toLocaleString("ru-RU")}{exportGoal ? ` из ${exportGoal.toLocaleString("ru-RU")}` : ""} записей…</span>
        <progress aria-label="Ход выгрузки" max={exportGoal || undefined} value={exportGoal ? exportCount : undefined}/>
        <button type="button" onClick={() => {
          if (activeExport.current) { activeExport.current.stop = true; activeExport.current.request?.cancel(); }
        }}>Остановить и сохранить</button>
      </div>}
      {exportMessage && <p role="status">{exportMessage}</p>}
      {error && <div role="alert" className="device-logs__error">{error} <button type="button" disabled={exporting} onClick={() => reset()}>Повторить</button></div>}
      {!loading && !error && !logs.length && <div className="device-logs__empty">За этот период и по этому запросу записей нет. Измените фильтры или выберите всё время.</div>}
      <div ref={listRef}>{pagination}</div>
      <div className={`device-logs__list device-logs__list--${view}`} aria-busy={loading}>
        {logs.map(log => <article className="device-logs__entry" key={log.id}>
          <div className="device-logs__time"><time dateTime={log.insertDateTime}>{dateText(log.insertDateTime)}</time>
            <span className={`device-logs__tag device-logs__tag--${log.kind || "unknown"}`}>{kinds[log.kind || "unknown"] ?? "Событие"}</span>
          </div>
          <div className="device-logs__message">
            {view !== "raw" && <div className="device-logs__explanation"><h3>{log.title}</h3><p>{log.detail}</p></div>}
            <Original log={log} expanded={view !== "explain"}/>
          </div>
        </article>)}
      </div>
      <footer>{pagination}</footer>
      {page && !page.hasMore && !loading && !error && logs.length > 0 && <p className="device-logs__footnote">Это самые ранние сохранённые записи выбранной выборки.</p>}
      <p className="device-logs__footnote">Время показано в часовом поясе браузера. Поиск выполняется по исходному тексту во всей истории.
        Пояснения описывают событие на момент записи, а не состояние станции сейчас. Одна запись может содержать пакет из нескольких событий.
        Выгрузка JSONL содержит исходные данные и пояснения; последняя строка _export сообщает количество и признак завершения.
        Большие архивы удобнее выгружать по периодам.</p>
    </section>
  );
};

// Switching stations remounts the view, cancelling stale requests and exports.
const DeviceLogs = ({deviceId}: Props) => <LogsHistory key={deviceId} deviceId={deviceId}/>;
export default DeviceLogs;
